import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { ImportProgress } from '../../../../types/domain-management.types';

@Component({
    selector: 'mon-import-wizard-progress',
    templateUrl: './progress.component.html',
    styleUrls: ['./progress.component.scss'],
    standalone: false,
})
export class ImportWizardProgressComponent implements OnInit, OnDestroy {
    @Input() progress: ImportProgress | null = null;

    // Animation and display properties
    animatedProgress = 0;
    lastProgressTime = Date.now();
    estimatedTimeRemaining = 0;
    importStartTime = 0;
    domainsPerSecond = 0;

    private animationFrame: number | null = null;
    private progressSubscription: Subscription | null = null;

    ngOnInit (): void {
        this.importStartTime = Date.now();
        this.startProgressAnimation();
    }

    ngOnDestroy (): void {
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
        }
        if (this.progressSubscription) {
            this.progressSubscription.unsubscribe();
        }
    }

    /**
     * Start smooth progress bar animation
     */
    private startProgressAnimation (): void {
        const animate = (): void => {
            if (this.progress) {
                const targetProgress = this.getProgressPercentage();
                const diff = targetProgress - this.animatedProgress;

                // Smooth animation towards target
                if (Math.abs(diff) > 0.1) {
                    this.animatedProgress += diff * 0.1;
                } else {
                    this.animatedProgress = targetProgress;
                }

                // Calculate statistics
                this.calculateStatistics();
            }

            this.animationFrame = requestAnimationFrame(animate);
        };

        animate();
    }

    /**
     * Calculate import statistics
     */
    private calculateStatistics (): void {
        if (!this.progress || this.progress.current === 0) {
            return;
        }

        const currentTime = Date.now();
        const elapsedSeconds = (currentTime - this.importStartTime) / 1000;

        if (elapsedSeconds > 0) {
            this.domainsPerSecond = this.progress.current / elapsedSeconds;

            const remainingDomains = this.progress.total - this.progress.current;
            if (this.domainsPerSecond > 0) {
                this.estimatedTimeRemaining = remainingDomains / this.domainsPerSecond;
            }
        }
    }

    /**
     * Get progress percentage
     */
    getProgressPercentage (): number {
        if (!this.progress || this.progress.total === 0) {
            return 0;
        }
        return Math.min(100, (this.progress.current / this.progress.total) * 100);
    }

    /**
     * Get progress bar color based on completion
     */
    getProgressBarColor (): string {
        const percentage = this.getProgressPercentage();
        if (percentage < 30) {
            return 'bg-info';
        } else if (percentage < 70) {
            return 'bg-primary';
        } else if (percentage < 100) {
            return 'bg-warning';
        } else {
            return 'bg-success';
        }
    }

    /**
     * Format time duration
     */
    formatTime (seconds: number): string {
        if (seconds < 60) {
            return `${Math.round(seconds)}s`;
        } else if (seconds < 3600) {
            const minutes = Math.floor(seconds / 60);
            const remainingSeconds = Math.round(seconds % 60);
            return `${minutes}m ${remainingSeconds}s`;
        } else {
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            return `${hours}h ${minutes}m`;
        }
    }

    /**
     * Format rate display
     */
    formatRate (rate: number): string {
        if (rate < 1) {
            return `${(rate * 60).toFixed(1)}/min`;
        } else {
            return `${rate.toFixed(1)}/sec`;
        }
    }

    /**
     * Get elapsed time since import started
     */
    getElapsedTime (): number {
        return (Date.now() - this.importStartTime) / 1000;
    }

    /**
     * Check if import is completed
     */
    isCompleted (): boolean {
        return this.progress?.current === this.progress?.total && this.progress?.total > 0;
    }

    /**
     * Get completion message
     */
    getCompletionMessage (): string {
        if (this.isCompleted()) {
            return `Successfully imported ${this.progress?.total} domains in ${this.formatTime(this.getElapsedTime())}`;
        }
        return this.progress?.message || 'Processing...';
    }
}
